import React from 'react';
import { ChevronDown } from 'react-feather';
import { Tooltip } from '@/shared/components/Tooltip';

const LABEL_COLOR_NEUTRAL = '#6a7278';
const LABEL_COLOR_FOCUSED = '#2a4455';

export interface DropDownButtonProps {
  /** Label text (e.g. placeholder / field name). When value is provided and differs, this floats up like TextField. */
  label: React.ReactNode;
  /** Whether the dropdown is open (e.g. for chevron rotation and floating state) */
  open: boolean;
  /** Called when the button is clicked to toggle the dropdown */
  toggle: () => void;
  /** When true, button is disabled and uses disabled styles */
  disabled?: boolean;
  /** When true, value cannot be changed; uses cursor-default and does not open dropdown */
  readOnly?: boolean;
  /** Accepted for API parity with the parent Dropdown's error prop. The border is what
   *  signals the error state (styled by the parent Dropdown); the label/value text stays
   *  its normal color, matching TextField's error styling. */
  error?: boolean;
  /** Additional class name for the button */
  className?: string;
  /**
   * Selected value(s) to display. When set and different from label, the label uses floating animation (TextField-style).
   * When undefined or same as label, label is shown as single line (no float).
   */
  value?: string | string[];
  /** When true, the chevron icon is hidden (e.g. when there is only one option). */
  hideChevron?: boolean;
  /** When true, show tooltip for selected items. Otherwise, wrap text. */
  toolTip?: boolean | undefined;
  /** Test identifier forwarded to the root button element */
  'data-testid'?: string;
}

/**
 * Reusable trigger button for Dropdown with TextField-style look.
 * Supports optional floating label animation when value is provided and differs from label.
 */
const DropDownButton: React.FC<DropDownButtonProps> = ({
  label,
  value,
  open,
  toggle,
  disabled = false,
  readOnly = false,
  className = '',
  hideChevron = false,
  toolTip = false,
  'data-testid': dataTestId,
}) => {
  const hasValue =
    value != null &&
    (Array.isArray(value) ? value.length > 0 : value !== '' && value !== label);
  const isFloating = open || hasValue;
  const labelColor = open ? LABEL_COLOR_FOCUSED : LABEL_COLOR_NEUTRAL;
  const displayText = Array.isArray(value)
    ? value.join(', ')
    : hasValue
      ? value
      : '';

  // Fixed content height so trigger doesn't grow when value is selected (matches TextField ~40px content)
  const contentHeight = 40;
  const labelPlaceholderHeight = 20; // 14px placeholder with 20px line height
  const labelSmallHeight = 12;
  const valueLineHeight = 24;
  const labelTopWhenFloating = 0;
  const valuePaddingTopWhenFloating = labelSmallHeight + 4;
  const valuePaddingBottomWhenFloating = 8;
  // Include bottom padding so the empty (no value) span and the filled value span
  // render at the same height; otherwise selecting a value grows the box and the
  // vertically-centered floating label shifts up a few pixels.
  const valueMinHeightWhenFloating =
    valueLineHeight +
    valuePaddingTopWhenFloating +
    valuePaddingBottomWhenFloating;

  const valueSpan = (
    <span
      className="block w-full text-base leading-6 overflow-hidden text-ellipsis whitespace-nowrap"
      style={{
        fontSize: 16,
        lineHeight: `${valueLineHeight}px`,
        paddingTop: isFloating ? valuePaddingTopWhenFloating : 0,
        paddingBottom: isFloating ? valuePaddingBottomWhenFloating : 0,
        minHeight: isFloating ? valueMinHeightWhenFloating : 0,
        transition: 'padding-top 200ms ease-out, min-height 200ms ease-out',
      }}
    >
      {displayText}
    </span>
  );

  return (
    <button
      type="button"
      data-testid={dataTestId}
      onClick={disabled || readOnly ? undefined : toggle}
      aria-expanded={open}
      aria-haspopup="true"
      aria-disabled={disabled}
      className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${className} ${
        disabled
          ? 'cursor-not-allowed text-neutral-60'
          : readOnly
            ? 'cursor-default text-neutral-10'
            : 'cursor-pointer text-neutral-10'
      }`}
    >
      <div
        className="flex-1 min-w-0 relative flex flex-col justify-center"
        style={{ minHeight: contentHeight }}
      >
        {/* Floating label (TextField-style): centered when not floating, top when floating */}
        <span
          className="absolute left-0 pointer-events-none origin-top-left font-normal"
          style={{
            color: labelColor,
            top: isFloating
              ? labelTopWhenFloating
              : (contentHeight - labelPlaceholderHeight) / 2,
            fontSize: isFloating ? 12 : 16,
            lineHeight: isFloating ? '16px' : `${labelPlaceholderHeight}px`,
            transition:
              'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
          }}
        >
          {label}
        </span>
        {/* Value text: truncate with optional tooltip in trigger */}
        {displayText ? (
          <div className="overflow-hidden min-w-0 w-full">
            {toolTip ? (
              <Tooltip
                content={displayText}
                contentMaxWidth={400}
                align="center"
              >
                {valueSpan}
              </Tooltip>
            ) : (
              valueSpan
            )}
          </div>
        ) : (
          <span
            className="block w-full text-base leading-6"
            style={{
              fontSize: 16,
              lineHeight: `${valueLineHeight}px`,
              paddingTop: isFloating ? valuePaddingTopWhenFloating : 0,
              paddingBottom: isFloating ? valuePaddingBottomWhenFloating : 0,
              minHeight: isFloating ? valueMinHeightWhenFloating : 0,
              transition:
                'padding-top 200ms ease-out, min-height 200ms ease-out',
            }}
          />
        )}
      </div>
      {!hideChevron && (
        <div
          className={`p-2 rounded-full transition-colors ${disabled ? 'cursor-not-allowed' : 'hover:bg-black/5 cursor-pointer'}`}
        >
          <ChevronDown
            strokeWidth={1.5}
            className={`shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          />
        </div>
      )}
    </button>
  );
};

export default DropDownButton;
